<template>
  <div v-if="!showScreenshotDialog" class="screenshot-container">
    <div class="screenshot-close">
      <v-btn color="white" fab @click="emit('update:value', false)">
        <bm-icon icon="Cancel" />
      </v-btn>
    </div>
    <div
      v-if="!isGeneratingScreenshot"
      :class="{
        'screenshot-overlay': true,
        selecting: isSelectingScreenshot,
      }"
      :style="{ borderWidth: borderWidth }"
      @mousemove="pointerMove"
      @touchmove="pointerMove"
      @mousedown="pointerDown"
      @touchstart="pointerDown"
      @mouseup="pointerUp"
      @touchend="pointerUp"
    >
      <v-card
        v-if="!isSelectingScreenshot"
        class="tooltip"
        :style="{
          left: tooltipCoordinates.x + 'px',
          top: tooltipCoordinates.y + 'px',
        }"
      >
        {{ $m.support_click_or_drag }}
      </v-card>
    </div>
  </div>
  <send-screenshot-dialog
    v-else
    :img-url="imgUrl"
    :screenshot-failed="screenshotFailed"
    @accept="sendScreenshot"
    @cancel="closeScreenshotDialog"
  />
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { debounce } from "lodash-es";
import html2canvas from "html2canvas";
import { withLoading } from "@/components/composables/componentTools";
import SendScreenshotDialog from "@/components/controls/support/SendScreenshotDialog.vue";
import { supportToolsService } from "@/app/services/generated/supporttoolsService";
import { useRouter } from "@/router/router";
import appStore from "@/store/modules/appStore";
import { detectBrowser } from "@/app/ui/utilities";
import { canvasElementToUint8Array } from "@/app/apiutils";
import userStore from "@/store/modules/userStore";
import { makeRef } from "@/components/composables/reactivityTypes";

interface EventCoordinates {
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
}

interface PositionCoordinates {
  x: null | number;
  y: null | number;
}

interface ScreenshotCoordinates {
  current: PositionCoordinates;
  start: PositionCoordinates;
  offset: PositionCoordinates;
}

interface ScreenShotToolProps {
  value?: boolean;
}

interface ScreenShotToolEvents {
  (event: "update:value", value: boolean): void;
}

/**
 * Screen coordinates are relative to the view port itself
 * Page coorindates are relative to the physical location on the page
 *
 * IE: Putting the cursor at the top left when you scroll down 100px would give:
 *  Screen(0, 0)    - This is what the tooltip is relative to
 *  Page  (0, 100)  - This is what the screenshot is relative to
 */
defineProps<ScreenShotToolProps>();
const emit = defineEmits<ScreenShotToolEvents>();

const showScreenshotDialog = makeRef(false);
const isSelectingScreenshot = makeRef(false);
const isGeneratingScreenshot = makeRef(false);
const screenshotFailed = makeRef(false);
const imgUrl = makeRef("");
const screenshotCoordinates = reactive<ScreenshotCoordinates>({
  start: {
    x: null,
    y: null,
  },
  current: {
    x: null,
    y: null,
  },
  // Used for if the page has scrolling
  offset: {
    x: null,
    y: null,
  },
});
// Where the tooltip should position, relative to the screen, not the page
const tooltipCoordinates = reactive<PositionCoordinates>({
  x: null,
  y: null,
});
const { route } = useRouter();

let screenshotImageData: Uint8Array | null = null;

const isTouch = (event: MouseEvent | TouchEvent): event is TouchEvent => {
  return (event as TouchEvent).touches !== undefined;
};

const getPositionFromEvent = (
  event: MouseEvent | TouchEvent
): EventCoordinates => {
  const eventItem = isTouch(event)
    ? event.touches.item(0)! // Select first touch event for simplicity
    : event;

  const { clientX, clientY, pageX, pageY } = eventItem;

  return { clientX, clientY, pageX, pageY };
};

const setCoordinates = (
  event: MouseEvent | TouchEvent,
  type: keyof ScreenshotCoordinates
) => {
  if (isGeneratingScreenshot.value) {
    return;
  }

  const { clientX, clientY, pageX, pageY } = getPositionFromEvent(event);

  const x = type === "offset" ? pageX - clientX : clientX;
  const y = type === "offset" ? pageY - clientY : clientY;

  screenshotCoordinates[type].x = x;
  screenshotCoordinates[type].y = y;

  if (type !== "offset") {
    tooltipCoordinates.x = x + 5;
    tooltipCoordinates.y = y + 5;
  }
};

const pointerDown = (event: MouseEvent | TouchEvent) => {
  setCoordinates(event, "start");
  setCoordinates(event, "current");
  setCoordinates(event, "offset");
  document.documentElement.classList.add("hide-overflow");
  isSelectingScreenshot.value = true;
};

const pointerMove = debounce((event: MouseEvent | TouchEvent) => {
  setCoordinates(event, "current");
});

const pointerUp = withLoading(async () => {
  isSelectingScreenshot.value = false;
  document.documentElement.classList.remove("hide-overflow");

  await generateScreenshot();
});

const generateCanvasData = async () => {
  // Fetch our data and create the canvas element
  return await html2canvas(document.body, {
    allowTaint: false,
    useCORS: true,
    ignoreElements: (node) => {
      const stylesBefore = window.getComputedStyle(node, ":before");
      const stylesAfter = window.getComputedStyle(node, ":after");
      const stylesFirstLetter = window.getComputedStyle(node, ":first-letter");
      const stylesFirstLine = window.getComputedStyle(node, ":first-line");
      const stylesSelection = window.getComputedStyle(node, ":selection");

      // Removing SVG styles needed to make Firefox to work
      // Removing the screenshot container was needed to make Safari work (trial and error, no idea why)
      return (
        stylesBefore["backgroundImage"].indexOf(".svg") !== -1 ||
        stylesAfter["backgroundImage"].indexOf(".svg") !== -1 ||
        stylesFirstLetter["backgroundImage"].indexOf(".svg") !== -1 ||
        stylesFirstLine["backgroundImage"].indexOf(".svg") !== -1 ||
        stylesSelection["backgroundImage"].indexOf(".svg") !== -1 ||
        node.className === "screenshot-container"
      );
    },
  });
};

const generateScreenshot = async () => {
  if (isGeneratingScreenshot.value) {
    return;
  }

  try {
    const { x: startX, y: startY } = screenshotCoordinates.start;
    const { x: endX, y: endY } = screenshotCoordinates.current;
    const { x: offsetX, y: offsetY } = screenshotCoordinates.offset;

    // Screens using zoom will need scaling (ie: 4k at 200% scaling will be offset)
    const canvasZoom = devicePixelRatio;
    const croppedCanvasWidth = Math.abs(endX! - startX!) * canvasZoom;
    const croppedCanvasHeight = Math.abs(endY! - startY!) * canvasZoom;

    if (croppedCanvasWidth !== 0 && croppedCanvasHeight !== 0) {
      isGeneratingScreenshot.value = true;
      const canvasData = await generateCanvasData();

      let canvasElement = document.createElement("canvas");
      let canvasContext = canvasElement.getContext("2d");

      // Draw our entire image, and keep a copy of the UInt8Array (support might value context)
      canvasElement.width = window.innerWidth;
      canvasElement.height = window.innerHeight;
      canvasContext?.drawImage(canvasData, 0, 0);

      canvasElement.width = croppedCanvasWidth;
      canvasElement.height = croppedCanvasHeight;

      // Draw the users selected region
      canvasContext?.drawImage(
        canvasData,
        (Math.min(startX!, endX!) + (offsetX || 0)) * canvasZoom, // Our position may also be offset
        (Math.min(startY!, endY!) + (offsetY || 0)) * canvasZoom, // We also want the top left, to get the min values
        croppedCanvasWidth,
        croppedCanvasHeight,
        0,
        0,
        croppedCanvasWidth,
        croppedCanvasHeight
      );

      // NB: Put this above the width/height cropping above, that should do the full image instead
      // (slower, but more useful if the screenshot is weird/wrong)
      screenshotImageData = await canvasElementToUint8Array(canvasElement);

      // Store our selected region as a URL for the screen to show
      imgUrl.value = canvasElement.toDataURL("image/jpeg");
    }

    showScreenshotDialog.value = true;

    // Once we've loaded our screenshot, reset our start pos
    screenshotCoordinates.start.x = null;
    screenshotCoordinates.start.y = null;
    screenshotCoordinates.current.x = null;
    screenshotCoordinates.current.y = null;
    screenshotCoordinates.offset.x = null;
    screenshotCoordinates.offset.y = null;
  } catch {
    isGeneratingScreenshot.value = false;
    screenshotFailed.value = true;
    showScreenshotDialog.value = true;
  }
};

// Checks that both .start and .current have defined values for all x and y pairs
const hasInvalidHighlightCoordinates = () => {
  return Object.values(screenshotCoordinates).some(
    (coordinate) => coordinate.x === null || coordinate.y === null
  );
};

const borderWidth = computed(() => {
  // Ensures we can build a correct border width
  if (hasInvalidHighlightCoordinates()) {
    return "";
  }

  const { x: startX, y: startY } = screenshotCoordinates.start;
  const { x: currentX, y: currentY } = screenshotCoordinates.current;
  const { innerWidth: width, innerHeight: height } = window;

  // The screenshot is stretching towards the right
  const leansRight = currentX! >= startX!;

  // The screenshot is stretching downwards
  const leansDown = currentY! >= startY!;

  if (leansRight) {
    return `${leansDown ? startY! : currentY!}px
        ${width - currentX!}px
        ${height - (leansDown ? currentY! : startY!)}px
        ${startX}px`;
  }

  return `${leansDown ? startY! : currentY!}px
      ${width - startX!}px
      ${height - (leansDown ? currentY! : startY!)}px
      ${currentX}px`;
});

const sendScreenshot = withLoading(async (feedback: string) => {
  const richFeedback = `Browser: ${detectBrowser()}
URL: ${route.value.fullPath}
PageTitle: ${appStore.state.documentTitle}
Feedback: ${feedback}
UserContact: ${userStore.state.user?.email ?? "Unknown"}`;

  await supportToolsService.sendUserFeedback(richFeedback, screenshotImageData);

  // Reset and close the UI
  screenshotFailed.value = false;
  showScreenshotDialog.value = false;
  emit("update:value", false);
});

const closeScreenshotDialog = () => {
  screenshotFailed.value = false;
  showScreenshotDialog.value = false;
  emit("update:value", false);
};
</script>

<style lang="sass">
@import "src/sass/colours"

html.hide-overflow
  overflow: hidden

.screenshot-container
  position: absolute
  cursor: crosshair

.screenshot-overlay
  background-color: rgba(0, 0, 0, 0.25)

  // Align the overlay to the entire screen
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%

  z-index: 1000 // Above everything, except our close/accept form

.screenshot-overlay.selecting
  background: none
  border-color: rgba(0, 0, 0, 0.33)
  border-style: solid

.screenshot-close
  position: fixed
  top: 2em
  right: 2em
  z-index: 1001 // Above the screenshot overlay

.tooltip
  position: fixed
  background-color: white
  color: $bm-color-text01
</style>
